// Singleton store cho unread count team-chat — source of truth duy nhất.
// Sống ngoài React tree → SidebarPrimary, TeamChatPushNotifications, useTeamChatStore
// đều subscribe để cùng đồng bộ qua useSyncExternalStore.
//
// Strategy:
//  - STOMP message.created → incrementRoom (đánh dấu lastIncrementAt = now)
//  - User mở room + scroll tới đáy → markRoomRead (xoá count + lastIncrementAt)
//  - Poll listRooms 60s → mergeFromServer trust BE TRỪ KHI local có STOMP increment
//    sau khi fetch bắt đầu (tránh race STOMP-trong-lúc-fetch ghi đè ngược).
//
// Persist localStorage để F5 không mất badge.

const STORAGE_KEY = 'chat:team_unread_counts';
const STORAGE_KEY_DELIVERED = 'chat:team_delivered_msg'; // roomId → lastDeliveredMessageId
                                                          // = tin nhắn cuối client đã nhận (STOMP / catchup REST).
                                                          // Dùng làm cursor cho catchup khi STOMP reconnect.
                                                          // KHÔNG bằng "đã đọc" — riêng badge clear qua markRoomRead.
const STORAGE_KEY_LAST_READ_AT = 'chat:team_last_read_at'; // roomId → timestamp markRoomRead cuối.
                                                            // Persist để RECENT_READ_MS window (60s) survive F5:
                                                            // user đọc → F5 ngay → poll listRooms về count cũ
                                                            // do chat-service mirror lag → mergeFromServer skip
                                                            // chỉ khi còn nhớ lastReadAt. In-memory mất sau F5.

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Map();
    const obj = JSON.parse(raw);
    return new Map(Object.entries(obj).filter(([_, v]) => v > 0));
  } catch { return new Map(); }
}

function persistToStorage(map) {
  try {
    const obj = {};
    for (const [k, v] of map.entries()) if (v > 0) obj[k] = v;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {}
}

function loadDelivered() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_DELIVERED);
    if (!raw) return new Map();
    return new Map(Object.entries(JSON.parse(raw)));
  } catch { return new Map(); }
}

function persistDelivered(map) {
  try {
    const obj = {};
    for (const [k, v] of map.entries()) if (v) obj[k] = v;
    localStorage.setItem(STORAGE_KEY_DELIVERED, JSON.stringify(obj));
  } catch {}
}

// _lastReadAt persist: chỉ giữ entry trong RECENT_READ_MS+buffer (5 phút).
// Entry quá hạn không còn cần cho race protection → drop khi load để Map
// không phình to vô hạn theo số room user từng đọc.
const LAST_READ_AT_TTL_MS = 5 * 60_000;
function loadLastReadAt() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY_LAST_READ_AT);
    if (!raw) return new Map();
    const now = Date.now();
    const m = new Map();
    for (const [k, v] of Object.entries(JSON.parse(raw))) {
      const t = Number(v);
      if (Number.isFinite(t) && now - t < LAST_READ_AT_TTL_MS) m.set(k, t);
    }
    return m;
  } catch { return new Map(); }
}

function persistLastReadAt(map) {
  try {
    const now = Date.now();
    const obj = {};
    for (const [k, v] of map.entries()) {
      if (Number.isFinite(v) && now - v < LAST_READ_AT_TTL_MS) obj[k] = v;
    }
    localStorage.setItem(STORAGE_KEY_LAST_READ_AT, JSON.stringify(obj));
  } catch {}
}

const _counts = loadFromStorage();           // roomId → number
const _lastIncrementAt = new Map();          // roomId → timestamp khi STOMP increment cuối
const _lastReadAt = loadLastReadAt();        // roomId → timestamp khi markRoomRead cuối (persist)
const _deliveredMessageId = loadDelivered(); // roomId → msgId cuối client đã nhận (persist)
const _listeners = new Set();

function emit() {
  persistToStorage(_counts);
  for (const l of _listeners) {
    try { l(); } catch (e) { console.error('[teamChatUnread] listener error', e); }
  }
}

export function getRoomUnread(roomId) {
  return _counts.get(roomId) || 0;
}

export function getTotalUnread() {
  let total = 0;
  for (const v of _counts.values()) total += v || 0;
  return total;
}

// Snapshot dùng cho consumer cần shape { [roomId]: count } trong useMemo.
// Cached để useSyncExternalStore không infinite-loop (cùng reference khi không đổi).
let _snapshotCache = null;
let _snapshotDirty = true;
function getSnapshot() {
  if (_snapshotDirty || !_snapshotCache) {
    const obj = {};
    // Include rooms với count > 0 AS-IS.
    for (const [k, v] of _counts.entries()) if (v > 0) obj[k] = v;
    // Include rooms ĐÃ MARK READ (có lastReadAt) với value 0 — để tile bỏ
    // dùng fallback `item.unread_count` từ adapter (chưa kịp refresh sau
    // click). User click conv → markRoomRead clear singleton → snapshot
    // phải REPLY 0 (chứ KHÔNG undefined → fallback ngược lại số cũ).
    for (const k of _lastReadAt.keys()) {
      if (!(k in obj)) obj[k] = 0;
    }
    _snapshotCache = obj;
    _snapshotDirty = false;
  }
  return _snapshotCache;
}

function markDirty() {
  _snapshotDirty = true;
}

export function setRoomUnread(roomId, count) {
  if (!roomId) return;
  const c = Math.max(0, Number(count) || 0);
  const cur = _counts.get(roomId) || 0;
  if (c === cur) return;
  if (c === 0) _counts.delete(roomId);
  else _counts.set(roomId, c);
  markDirty();
  emit();
}

export function incrementRoom(roomId, by = 1) {
  if (!roomId) return;
  const cur = _counts.get(roomId) || 0;
  _counts.set(roomId, cur + by);
  _lastIncrementAt.set(roomId, Date.now());
  markDirty();
  emit();
}

// Emergency reset — user dính badge sai (vd singleton localStorage stale) có thể
// clear hết counts. Backend sẽ re-seed qua mergeFromServer ở fetchAndSync tiếp.
export function clearAllUnread() {
  if (_counts.size === 0) return;
  // Capture keys TRƯỚC khi clear — bug cũ: _counts.clear() chạy trước vòng lặp
  // nên [..._counts.keys()] rỗng → _lastReadAt không được set → mất guard.
  const roomIds = [..._counts.keys()];
  _counts.clear();
  _lastIncrementAt.clear();
  const now = Date.now();
  for (const roomId of roomIds) _lastReadAt.set(roomId, now);
  persistLastReadAt(_lastReadAt);
  markDirty();
  emit();
}

// User mở room + đã scroll tới đáy → xoá badge.
// Cập nhật deliveredMessageId tới upToMessageId để catchup không replay tin cũ.
// Set lastReadAt để mergeFromServer KHÔNG ghi đè ngược badge từ fetch start trước đó.
export function markRoomRead(roomId, upToMessageId = null) {
  if (!roomId) return;
  let countChanged = false;
  if (_counts.has(roomId)) {
    _counts.delete(roomId);
    countChanged = true;
  }
  _lastIncrementAt.delete(roomId);
  const prevReadAt = _lastReadAt.get(roomId);
  _lastReadAt.set(roomId, Date.now());
  persistLastReadAt(_lastReadAt);
  const readAtChanged = !prevReadAt; // first time mark-read cho room
  if (upToMessageId) setDeliveredMessageId(roomId, upToMessageId, { forwardOnly: true });
  // Emit cho mọi trường hợp: count đổi, lastReadAt mới (room first read), HOẶC
  // luôn (defensive — đảm bảo snapshot include {roomId: 0} entry kịp thời cho
  // subscribers). Trước đây chỉ emit khi countChanged → bug: room mới mark-read
  // lần đầu nhưng singleton chưa có count → no emit → sidebar không refresh
  // ngay → badge cũ từ adapter (c.unread_count) vẫn hiện.
  if (countChanged || readAtChanged) {
    markDirty();
    emit();
  }
}

// Delivered cursor — tin cuối client đã nhận (STOMP message.created handler / catchup REST).
// Dùng cho fetchAndSync({catchupMissed:true}) làm cursor `?after=<id>` khi STOMP reconnect.
export function getDeliveredMessageId(roomId) {
  return _deliveredMessageId.get(roomId) || null;
}

export function setDeliveredMessageId(roomId, msgId, { forwardOnly = false } = {}) {
  if (!roomId || !msgId) return;
  const cur = _deliveredMessageId.get(roomId);
  if (cur === msgId) return;
  // forwardOnly: chặn cursor CHẠY LÙI khi mark-read mang id cũ (user cuộn ngược đọc
  // lịch sử → viewport scan trả id cũ hơn). Cursor lùi = STOMP reconnect replay
  // `?after=<id cũ>` → incrementRoom hàng chục tin đã đọc + bắn lại notification.
  // Đường NHẬN TIN (STOMP message.created) vẫn ghi thẳng (không forwardOnly) vì id ở
  // đó luôn là tin mới nhất vừa nhận.
  if (forwardOnly && cur && msgId <= cur) return; // UUIDv7 → so chuỗi = so thời gian
  _deliveredMessageId.set(roomId, msgId);
  persistDelivered(_deliveredMessageId);
}

// Seed từ BE listRooms — TRUST BE (BE = source of truth, cho phép TĂNG lẫn GIẢM).
//
// entries: Array<[roomId, count]> — TẤT CẢ room BE biết (kể cả count=0).
// fetchStartedAt: timestamp khi fetch bắt đầu — race protection.
//
// MÔ HÌNH MỚI (Task 1, 2026-06-03): bỏ MAX-strategy + RECENT_READ_MS.
// Lý do: đã xác minh BE (chat-service) tính unread bằng query trực tiếp DB mỗi
// request (countUnreadByRooms theo lastReadMessageId), KHÔNG có mirror/replica/cache
// → KHÔNG có "lag". markRead là @Transactional đồng bộ → poll kế tiếp đã đúng.
// Mô hình MAX cũ (BE chỉ được TĂNG) dựa trên giả định sai "BE lag" → tự gây bất
// ổn: BE trả 0 đúng (đọc device khác / pointer đã advance) nhưng web bỏ qua → badge
// kẹt. Giờ trust BE → self-healing: poll kế tiếp tự sửa mọi sai lệch.
//
// CHỈ GIỮ 1 race-guard: STOMP increment / markRead xảy ra SAU khi fetch bắt đầu →
// BE chưa kịp biết sự kiện đó → giá trị BE cho room này đã cũ → giữ local. Đây là
// race thật (in-flight), khác hẳn "lag" tưởng tượng.
export function mergeFromServer(entries, fetchStartedAt = 0) {
  let changed = false;
  const beRoomIds = new Set();
  for (const [roomId, count] of entries) {
    beRoomIds.add(roomId);
    const c = Math.max(0, Number(count) || 0);
    const cur = _counts.get(roomId) || 0;
    // Race-guard: STOMP/markRead SAU fetchStartedAt → BE chưa biết → giữ local.
    if (fetchStartedAt > 0) {
      const lastInc = _lastIncrementAt.get(roomId) || 0;
      const lastRead = _lastReadAt.get(roomId) || 0;
      if (lastInc > fetchStartedAt || lastRead > fetchStartedAt) continue;
    }
    // Trust BE: set theo BE (lên HOẶC xuống). c==0 → xoá để map gọn.
    if (c === cur) continue;
    if (c === 0) _counts.delete(roomId); else _counts.set(roomId, c);
    changed = true;
  }
  // Purge stale: room không còn trong BE response (rời room / bị xoá / mất quyền)
  // → xoá. Giữ race-guard (STOMP vừa increment → giữ).
  if (fetchStartedAt > 0 && entries.length > 0) {
    for (const roomId of [..._counts.keys()]) {
      if (beRoomIds.has(roomId)) continue;
      const lastInc = _lastIncrementAt.get(roomId) || 0;
      if (lastInc > fetchStartedAt) continue;
      _counts.delete(roomId);
      _lastIncrementAt.delete(roomId);
      changed = true;
    }
  }
  if (changed) {
    markDirty();
    emit();
  }
}

export function subscribe(listener) {
  _listeners.add(listener);
  return () => _listeners.delete(listener);
}

// React hooks
import { useSyncExternalStore } from 'react';

export function useTeamChatUnreadTotal() {
  return useSyncExternalStore(
    (cb) => subscribe(cb),
    () => getTotalUnread(),
    () => 0,
  );
}

export function useTeamChatRoomUnread(roomId) {
  return useSyncExternalStore(
    (cb) => subscribe(cb),
    () => getRoomUnread(roomId),
    () => 0,
  );
}

// Snapshot { roomId: count } — useTeamChatStore expose qua field `unreadCounts`
// để các component cũ vẫn dùng được syntax `unreadCounts[id]` mà không cần đổi.
export function useTeamChatUnreadSnapshot() {
  return useSyncExternalStore(
    (cb) => subscribe(cb),
    () => getSnapshot(),
    () => ({}),
  );
}
